import type {
  FinanceSectorNewsItemApi,
  SectorNewsItem,
  SectorPanelData,
} from "./HotTopicsSection.types";

export function clampInt(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

export function normalizeBasePath(apiBasePath: string | undefined) {
  return (apiBasePath ?? "").replace(/\/+$/, "");
}

export function buildApiUrl(apiBasePath: string, pathAndQuery: string) {
  if (!apiBasePath) return pathAndQuery;
  const p = pathAndQuery.startsWith("/") ? pathAndQuery : `/${pathAndQuery}`;
  return `${apiBasePath}${p}`;
}

export async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`[HotTopicsSection] ${res.status} ${res.statusText}: ${body || url}`);
  }
  return (await res.json()) as T;
}

export function normalizeNewsItems(items: FinanceSectorNewsItemApi[] | undefined, limit: number) {
  const safe = Array.isArray(items) ? items : [];
  return safe
    .filter((n) => Boolean(n?.title) && Boolean(n?.url))
    .sort((a, b) => (b.publish_time ?? 0) - (a.publish_time ?? 0))
    .slice(0, Math.max(0, limit))
    .map((n) => ({
      publish_time: Number(n.publish_time ?? 0),
      title: String(n.title ?? ""),
      url: String(n.url ?? ""),
      summary: (n.summary ?? "").trim() || undefined,
    }));
}

export function formatPublishTime(sec: number) {
  const s = Number(sec ?? 0);
  if (!s) return "";
  const d = new Date(s * 1000);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatTickers(tickers: string[]) {
  return tickers.length ? tickers.join(", ") : "—";
}

export function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter((x) => x.length > 0);
}

export function pickFirstString(...vals: unknown[]) {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export function coerceCardToPanelData(
  card: unknown,
  fallbackLabel = "",
  fallbackCount = 3,
): SectorPanelData | null {
  if (!card || typeof card !== "object") return null;
  const c = card as any;

  const sectorId = pickFirstString(c.sectorId, c.sector_id, c.categoryKey, c.key);
  if (!sectorId) return null;

  const label = pickFirstString(c.label, c.title, c.categoryLabel) || fallbackLabel || sectorId;
  const count = clampInt(
    Number(c.limit ?? c.count ?? c.news?.length ?? c.items?.length ?? c.articles?.length ?? fallbackCount),
    1,
    9,
  );

  const rawNews: any[] =
    (Array.isArray(c.news) && c.news) ||
    (Array.isArray(c.items) && c.items) ||
    (Array.isArray(c.articles) && c.articles) ||
    (Array.isArray(c.data) && c.data) ||
    [];

  const news: SectorNewsItem[] = rawNews
    .filter((n) => n && typeof n === "object")
    .map((n) => {
      const publish_time = Number((n as any).publish_time ?? (n as any).publishTime ?? 0);
      const title = String((n as any).title ?? "");
      const url = String((n as any).url ?? "");
      const summary = String((n as any).summary ?? "").trim() || undefined;
      return { publish_time, title, url, summary };
    })
    .filter((n) => Boolean(n.title) && Boolean(n.url))
    .sort((a, b) => (b.publish_time ?? 0) - (a.publish_time ?? 0))
    .slice(0, count);

  const bullish = asStringArray(c.bullish ?? c.sentiment?.bullish);
  const bearish = asStringArray(c.bearish ?? c.sentiment?.bearish);

  return { sectorId, label, count, news, bullish, bearish };
}
